/**
 * Plan Configuration - Single Source of Truth
 * All plan definitions, limits, feature flags, and colors
 */

export type PlanId = 'trial' | 'individual' | 'small_clinic' | 'clinic_pro' | 'enterprise';

export interface PlanFeatureFlags {
  // Core Features (always available)
  calendar: boolean;
  basicReports: boolean;

  // Patient Experience Features
  onlineBooking: boolean; // Public booking page for patients
  patientPortal: boolean; // Patient portal with magic link access

  // Documents & Assessments
  documentTemplates: boolean; // Clinical document templates
  documentGeneration: boolean; // Auto-generate documents (receipts, declarations)
  assessments: boolean; // Psychological assessments/instruments

  // Advanced Scheduling
  waitingList: boolean; // Waiting list management with notifications

  // AI & Voice Features
  voiceToText: boolean; // Voice-to-text notes in real-time

  // Dashboards & Progress
  progressDashboard: boolean; // Patient progress tracking dashboard

  // Marketing & Growth
  referralProgram: boolean; // Referral/affiliate program

  // Paid Features
  financial: boolean;
  advancedReports: boolean;
  advancedDashboard: boolean;
  videoCall: boolean;
  aiInsights: boolean;
  aiTranscription: boolean; // Real-time transcription with Deepgram

  // Notification Channels
  emailNotifications: boolean;
  whatsappNotifications: boolean;

  // Premium Features
  apiAccess: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  dedicatedSupport: boolean;
}

export interface PlanLimits {
  maxUsers: number; // -1 for unlimited
  maxClients: number; // -1 for unlimited
  maxStorageMB: number; // -1 for unlimited
  trialDays?: number; // Only for trial plan
  aiTranscriptionsPerMonth?: number; // -1 for unlimited, undefined for not available
}

export interface PlanColors {
  badge: string;
  bg: string;
  text: string;
  border: string;
}

export interface PlanDefinition {
  id: PlanId;
  name: string;
  displayName: string;
  description: string;
  price: number;
  currency: 'BRL';
  billingInterval: 'month' | 'year';
  features: PlanFeatureFlags;
  limits: PlanLimits;
  colors: PlanColors;
  isPopular?: boolean;
}

// Plan colors - cohesive hierarchy
export const PLAN_COLORS: Record<PlanId, PlanColors> = {
  trial: {
    badge: '#718096',
    bg: '#e2e8f0',
    text: '#4a5568',
    border: '#cbd5e0',
  },
  individual: {
    badge: '#3b82f6',
    bg: '#dbeafe',
    text: '#1e40af',
    border: '#93c5fd',
  },
  small_clinic: {
    badge: '#10b981',
    bg: '#d1fae5',
    text: '#065f46',
    border: '#6ee7b7',
  },
  clinic_pro: {
    badge: '#f59e0b',
    bg: '#fef3c7',
    text: '#92400e',
    border: '#fcd34d',
  },
  enterprise: {
    badge: '#8b5cf6',
    bg: '#ede9fe',
    text: '#5b21b6',
    border: '#c4b5fd',
  },
};

export const PLAN_DEFINITIONS: Record<PlanId, PlanDefinition> = {
  trial: {
    id: 'trial',
    name: 'trial',
    displayName: 'Teste',
    description: '30 dias grátis para testar',
    price: 0,
    currency: 'BRL',
    billingInterval: 'month',
    features: {
      calendar: true,
      basicReports: true,
      onlineBooking: false,
      patientPortal: false,
      documentTemplates: false,
      documentGeneration: false,
      assessments: false,
      waitingList: false,
      voiceToText: false,
      progressDashboard: false,
      referralProgram: false,
      financial: true,
      advancedReports: false,
      advancedDashboard: false,
      videoCall: false,
      aiInsights: false,
      aiTranscription: false,
      emailNotifications: true,
      whatsappNotifications: false,
      apiAccess: false,
      customBranding: false,
      prioritySupport: false,
      dedicatedSupport: false,
    },
    limits: {
      maxUsers: 1,
      maxClients: 30,
      maxStorageMB: 500,
      trialDays: 30,
    },
    colors: PLAN_COLORS.trial,
  },
  individual: {
    id: 'individual',
    name: 'individual',
    displayName: 'Individual',
    description: 'Para profissionais autônomos',
    price: 19.90,
    currency: 'BRL',
    billingInterval: 'month',
    features: {
      calendar: true,
      basicReports: true,
      onlineBooking: true,
      patientPortal: true,
      documentTemplates: true,
      documentGeneration: true,
      assessments: true,
      waitingList: true,
      voiceToText: false,
      progressDashboard: true,
      referralProgram: false,
      financial: true,
      advancedReports: false,
      advancedDashboard: false,
      videoCall: true,
      aiInsights: false,
      aiTranscription: false,
      emailNotifications: true,
      whatsappNotifications: false,
      apiAccess: false,
      customBranding: false,
      prioritySupport: false,
      dedicatedSupport: false,
    },
    limits: {
      maxUsers: 1,
      maxClients: -1, // Unlimited
      maxStorageMB: 2048,
    },
    colors: PLAN_COLORS.individual,
  },
  small_clinic: {
    id: 'small_clinic',
    name: 'small_clinic',
    displayName: 'Clínica',
    description: 'Para clínicas com equipe',
    price: 49.90,
    currency: 'BRL',
    billingInterval: 'month',
    features: {
      calendar: true,
      basicReports: true,
      onlineBooking: true,
      patientPortal: true,
      documentTemplates: true,
      documentGeneration: true,
      assessments: true,
      waitingList: true,
      voiceToText: false,
      progressDashboard: true,
      referralProgram: false,
      financial: true,
      advancedReports: true,
      advancedDashboard: true,
      videoCall: true,
      aiInsights: false,
      aiTranscription: false,
      emailNotifications: true,
      whatsappNotifications: true,
      apiAccess: false,
      customBranding: false,
      prioritySupport: true,
      dedicatedSupport: false,
    },
    limits: {
      maxUsers: 5,
      maxClients: -1, // Unlimited
      maxStorageMB: 5120,
    },
    colors: PLAN_COLORS.small_clinic,
    isPopular: true,
  },
  clinic_pro: {
    id: 'clinic_pro',
    name: 'clinic_pro',
    displayName: 'Clínica Pro',
    description: 'Clínica com recursos de IA',
    price: 99.90,
    currency: 'BRL',
    billingInterval: 'month',
    features: {
      calendar: true,
      basicReports: true,
      onlineBooking: true,
      patientPortal: true,
      documentTemplates: true,
      documentGeneration: true,
      assessments: true,
      waitingList: true,
      voiceToText: true,
      progressDashboard: true,
      referralProgram: true,
      financial: true,
      advancedReports: true,
      advancedDashboard: true,
      videoCall: true,
      aiInsights: true,
      aiTranscription: true,
      emailNotifications: true,
      whatsappNotifications: true,
      apiAccess: false,
      customBranding: false,
      prioritySupport: true,
      dedicatedSupport: false,
    },
    limits: {
      maxUsers: 15,
      maxClients: -1, // Unlimited
      maxStorageMB: 10240,
      aiTranscriptionsPerMonth: 100, // Limited AI usage
    },
    colors: PLAN_COLORS.clinic_pro,
  },
  enterprise: {
    id: 'enterprise',
    name: 'enterprise',
    displayName: 'Enterprise',
    description: 'Recursos ilimitados com IA',
    price: 199.90,
    currency: 'BRL',
    billingInterval: 'month',
    features: {
      calendar: true,
      basicReports: true,
      onlineBooking: true,
      patientPortal: true,
      documentTemplates: true,
      documentGeneration: true,
      assessments: true,
      waitingList: true,
      voiceToText: true,
      progressDashboard: true,
      referralProgram: true,
      financial: true,
      advancedReports: true,
      advancedDashboard: true,
      videoCall: true,
      aiInsights: true,
      aiTranscription: true,
      emailNotifications: true,
      whatsappNotifications: true,
      apiAccess: true,
      customBranding: true,
      prioritySupport: true,
      dedicatedSupport: true,
    },
    limits: {
      maxUsers: -1, // Unlimited
      maxClients: -1, // Unlimited
      maxStorageMB: -1, // Unlimited
      aiTranscriptionsPerMonth: -1, // Unlimited AI usage
    },
    colors: PLAN_COLORS.enterprise,
  },
};

// Helper functions
export function getPlanById(planId: PlanId | string): PlanDefinition | undefined {
  return PLAN_DEFINITIONS[planId as PlanId];
}

export function getPlanFeature(planId: PlanId | string, feature: keyof PlanFeatureFlags): boolean {
  const plan = PLAN_DEFINITIONS[planId as PlanId];
  return plan?.features[feature] ?? false;
}

export function getPlanLimit(planId: PlanId | string, limit: keyof PlanLimits): number {
  const plan = PLAN_DEFINITIONS[planId as PlanId];
  const value = plan?.limits[limit];
  return typeof value === 'number' ? value : 0;
}

export function getPlanColors(planId: PlanId | string): PlanColors {
  return PLAN_COLORS[planId as PlanId] || PLAN_COLORS.trial;
}

export function isUnlimited(value: number): boolean {
  return value === -1;
}

export function getAllPlans(): PlanDefinition[] {
  return Object.values(PLAN_DEFINITIONS);
}

export function getPaidPlans(): PlanDefinition[] {
  return getAllPlans().filter(p => p.id !== 'trial');
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
}

export function formatLimit(value: number): string {
  if (isUnlimited(value)) return 'Ilimitado';
  return value.toString();
}

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES: Record<keyof PlanFeatureFlags, string> = {
  calendar: 'Agenda Integrada',
  basicReports: 'Relatórios Básicos',
  onlineBooking: 'Agendamento Online',
  patientPortal: 'Portal do Paciente',
  documentTemplates: 'Modelos de Documentos',
  documentGeneration: 'Geração de Documentos',
  assessments: 'Avaliações Psicológicas',
  waitingList: 'Fila de Espera',
  voiceToText: 'Voz para Texto',
  progressDashboard: 'Dashboard de Progresso',
  referralProgram: 'Programa de Indicação',
  financial: 'Módulo Financeiro Completo',
  advancedReports: 'Relatórios Avançados',
  advancedDashboard: 'Dashboard Avançado',
  videoCall: 'Teleconsulta (Videochamada)',
  aiInsights: 'Insights com IA',
  aiTranscription: 'Transcrição IA em Tempo Real',
  emailNotifications: 'Notificações por Email',
  whatsappNotifications: 'Notificações WhatsApp',
  apiAccess: 'Acesso Completo à API',
  customBranding: 'Marca Personalizada',
  prioritySupport: 'Suporte Prioritário',
  dedicatedSupport: 'Suporte Dedicado 24/7',
};

// Get list of enabled features for a plan as display strings
export function getFeatureList(planId: PlanId | string): string[] {
  const plan = PLAN_DEFINITIONS[planId as PlanId];
  if (!plan) return [];

  const features: string[] = [];

  Object.entries(plan.features).forEach(([key, enabled]) => {
    if (enabled) {
      const displayName = FEATURE_DISPLAY_NAMES[key as keyof PlanFeatureFlags];
      if (displayName) {
        features.push(displayName);
      }
    }
  });

  // Add limit info
  if (plan.limits.maxUsers === -1) {
    features.push('Usuarios Ilimitados');
  } else if (plan.limits.maxUsers > 1) {
    features.push(`Ate ${plan.limits.maxUsers} usuarios`);
  }

  if (plan.limits.maxClients === -1) {
    features.push('Pacientes Ilimitados');
  } else if (plan.limits.maxClients > 0) {
    features.push(`Ate ${plan.limits.maxClients} pacientes`);
  }

  return features;
}

// Get the minimum plan required for a feature
export function getMinimumPlanForFeature(feature: keyof PlanFeatureFlags): PlanId {
  const planOrder: PlanId[] = ['trial', 'individual', 'small_clinic', 'clinic_pro', 'enterprise'];

  for (const planId of planOrder) {
    if (PLAN_DEFINITIONS[planId].features[feature]) {
      return planId;
    }
  }

  return 'enterprise'; // Default to enterprise if not found
}

// Array of all plans (alias for compatibility)
export const PLANS = getAllPlans();

// Public plans - excludes trial (only accessible via admin invite key)
export const PUBLIC_PLANS = getPaidPlans();
